import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { fetchAddresses } from "api";
import { Spinner } from "components/common";
import type { ScheduleItem, EntityAddress } from "types";

function makeNumberedIcon(n: number, color: string) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35)">${n}</div>`,
    iconSize:   [28, 28],
    iconAnchor: [14, 14],
  });
}

function RouteMap({ stops }: {
  stops: Array<{
    item:      ScheduleItem;
    address:   EntityAddress | undefined;
    label:     string;
    isPickup:  boolean;
    stopNum:   number;
  }>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const stopsWithCoords = stops.filter((s) => s.address?.coordinates);
    const points: [number, number][] = stopsWithCoords.map((s) => [
      s.address!.coordinates!.lat,
      s.address!.coordinates!.lng,
    ]);

    const defaultCenter: [number, number] = points.length > 0
      ? points[0]
      : [41.8781, -87.6298];

    // Destroy previous map instance if re-rendering with new data
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerRef.current).setView(defaultCenter, 12);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    stopsWithCoords.forEach((s) => {
      L.marker(
        [s.address!.coordinates!.lat, s.address!.coordinates!.lng],
        { icon: makeNumberedIcon(s.stopNum, s.isPickup ? "#059669" : "#6366f1") }
      )
        .bindPopup(`<strong>${s.label}</strong><br/><span style="font-size:11px;color:#6b7280">${s.isPickup ? "Pickup" : "Drop off"}</span>`)
        .addTo(map);
    });

    if (points.length > 1) {
      L.polyline(points, { color: "#6366f1", weight: 3, dashArray: "8 5" }).addTo(map);
      map.fitBounds(points, { padding: [30, 30] });
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [stops]);

  return <div ref={containerRef} style={{ height: "100%", width: "100%", minHeight: "420px" }} />;
}

export function RouteMapModal({
  items,
  date,
  partnerNames,
  partnerTypes,
  onClose,
}: {
  items:        ScheduleItem[];
  date:         Date;
  partnerNames: Record<number, string>;
  partnerTypes: Record<number, string>;
  onClose:      () => void;
}) {
  const partnerIds = [...new Set(items.map((i) => i.partner_entity_id))];

  const { data: addressMap, isLoading } = useQuery({
    queryKey: ["addresses", "route", partnerIds],
    queryFn: async () => {
      const results = await Promise.all(
        partnerIds.map((id) => fetchAddresses({ partner_entity_id: String(id) }))
      );
      const map: Record<number, EntityAddress | undefined> = {};
      partnerIds.forEach((id, i) => { map[id] = results[i][0]; });
      return map;
    },
  });

  const stops = items.map((item, idx) => ({
    item,
    address:  addressMap?.[item.partner_entity_id],
    label:    partnerNames[item.partner_entity_id] ?? `Partner #${item.partner_entity_id}`,
    isPickup: partnerTypes[item.partner_entity_id] !== "distributor",
    stopNum:  idx + 1,
  }));

  const googleMapsUrl = stops.length > 0
    ? "https://www.google.com/maps/dir/" +
      stops.map((s) => {
        if (s.address?.coordinates) {
          return `${s.address.coordinates.lat},${s.address.coordinates.lng}`;
        }
        if (s.address) {
          const parts = [s.address.address, s.address.city, s.address.state, s.address.zip]
            .filter(Boolean).join(", ");
          return encodeURIComponent(parts);
        }
        return encodeURIComponent(s.label);
      }).join("/") + "/"
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">
            Route &mdash; {format(date, "EEEE, MMMM d")}
          </h2>
          <div className="flex items-center gap-3">
            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Open in Google Maps
              </a>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              &times;
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>
            {/* Map */}
            <div className="flex-1">
              <RouteMap stops={stops} />
            </div>

            {/* Stop list */}
            <div className="w-60 shrink-0 border-l border-gray-100 overflow-y-auto">
              <div className="p-4 space-y-4">
                {stops.map((s) => (
                  <div key={s.item.id} className="flex gap-3 items-start">
                    <div
                      className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5"
                      style={{ backgroundColor: s.isPickup ? "#059669" : "#6366f1" }}
                    >
                      {s.stopNum}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{s.label}</p>
                      <p className={`text-xs font-medium ${s.isPickup ? "text-green-600" : "text-indigo-600"}`}>
                        {s.isPickup ? "Pickup" : "Drop off"}
                      </p>
                      {s.address ? (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {s.address.address}{s.address.address_2 ? `, ${s.address.address_2}` : ""}, {s.address.city}
                        </p>
                      ) : (
                        <p className="text-xs text-amber-500 mt-0.5">No address on file</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
